/** @jsx jsx */
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Box, Button, Flex, Grid, Heading, jsx, Spinner, Text } from 'theme-ui'
import {
  useAnalytics,
  useTranslate,
} from '@chordcommerce/gatsby-theme-autonomy'
import RichTextRenderer from '~/components/Content/RichText'
import CheckboxInput from '~/components/Generic/Form/Checkbox'
import FormInputWithLabel from '~/components/Generic/Form/InputWithLabel'
import { useAria } from '~/hooks/components/use-aria'
import { captureSweepstakesEmail } from '~/services/functions/client'

const SweepstakesEmailCaptureSection = ({
  description,
  body,
  footer,
  klaviyoListId,
  sweepstakesId,
  images,
  success,
  title,
}) => {
  const { trackEmailCaptured } = useAnalytics()
  const { updateAriaLive } = useAria()
  const { register, handleSubmit, errors } = useForm()
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const translate = useTranslate()

  const isClosed = title.includes('[CLOSED]')
  const isSingleCheckbox = body?.raw?.includes('Maker Wine')

  const onSubmit = async (data) => {
    const { email, makerConsent, partnersConsent } = data
    const body = { email, makerConsent, partnersConsent, sweepstakesId }

    if (makerConsent) {
      body.klaviyoListId = klaviyoListId
    }

    if (isSingleCheckbox && partnersConsent) {
      body.klaviyoListId = klaviyoListId
      body.makerConsent = true
    }

    try {
      setError(null)
      setIsSubmitting(true)
      await captureSweepstakesEmail(body)

      trackEmailCaptured({
        emailCapture: {
          email,
          page: sweepstakesId,
          component: 'sweepstakes-email-capture',
          website: window.location.hostname,
        },
      })

      setIsSubmitting(false)
      setSubmitted(true)
      updateAriaLive('Your submission was successful.')
    } catch (err) {
      console.error(err, { source: 'SweepstakesEmailCaptureSection.onSubmit' })
      setError('Something went wrong. Please try again or contact support.')
      updateAriaLive(
        'Something went wrong. Please try again or contact support.'
      )
      setIsSubmitting(false)
    }
  }

  return (
    <Box>
      <Grid
        columns={[1, 12]}
        sx={{
          gridTemplateRows: images[1] && [
            'min-content 1fr',
            null,
            'repeat(1, 1fr)',
          ],
          rowGap: 0,
        }}
      >
        <Flex
          pb={[5, null, 7]}
          pl={[2, 0]}
          pr={[2, 0, 3]}
          pt={[5, null, 6]}
          sx={{
            flexDirection: 'column',
            gridColumn: ['1 / -1', '3 / 11', '7 / -1'],
            gridRow: ['2 / -1', null, '1 / 2'],
            justifyContent: 'center',
            maxWidth: [null, null, '573px'],
          }}
        >
          <Heading as="h1" variant="title2">
            {title}
          </Heading>
          {description && (
            <Box
              sx={{
                variant: 'text.body2',
                a: {
                  variant: 'text.link',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: description.childMarkdownRemark.html,
              }}
            />
          )}

          {!submitted && !isClosed && (
            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
              <Box mb={2}>
                <FormInputWithLabel
                  name="email"
                  title="Email address"
                  label="Email address"
                  placeholder="Email address"
                  aria-label="Email address"
                  ref={register({
                    required: true,
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: translate('validation.invalid_email'),
                    },
                  })}
                  error={
                    errors?.email?.type === 'required'
                      ? translate('validation.required_field')
                      : errors?.email?.type === 'pattern'
                      ? translate('validation.invalid_email')
                      : null
                  }
                  mb={[2, 0]}
                />
              </Box>
              {!isSingleCheckbox && (
                <Box mb={!!body ? 1 : 2}>
                  <CheckboxInput
                    defaultChecked={true}
                    name="makerConsent"
                    ref={register()}
                    label="I agree to receive email communication from Maker Wine."
                  />
                </Box>
              )}
              {body && (
                <Box mb={2} sx={{ p: { m: 0, variant: 'text.body3' } }}>
                  <CheckboxInput
                    defaultChecked={isSingleCheckbox}
                    name="partnersConsent"
                    ref={register()}
                    label={
                      <RichTextRenderer richText={body} optionSet="body" />
                    }
                  />
                </Box>
              )}
              <Button
                type="submit"
                variant="primary"
                sx={{ minWidth: '150px', width: ['100%', 'auto'] }}
              >
                {!isSubmitting && 'Enter'}
                {isSubmitting && (
                  <Spinner
                    size="24"
                    color="inherit"
                    mb={-1}
                    sx={{
                      position: 'relative',
                      top: '-2px',
                      '@media (prefers-reduced-motion)': {
                        circle: {
                          animationIterationCount: 8,
                        },
                      },
                    }}
                  />
                )}
              </Button>
            </Box>
          )}

          {error && (
            <Box mt={2}>
              <Text as="p" variant="error">
                {error}
              </Text>
            </Box>
          )}

          {submitted && (
            <Box sx={{ p: { fontWeight: 'bold', variant: 'text.body1' } }}>
              <RichTextRenderer richText={success} optionSet="text" />
            </Box>
          )}

          {isClosed && (
            <Heading as="h2" variant="title3" mb={2}>
              {'Submissions have been closed.'}
            </Heading>
          )}

          {footer && (
            <Box
              sx={{
                variant: 'text.finePrint',
                color: 'greyDark',
                a: {
                  variant: 'text.link',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: footer.childMarkdownRemark.html,
              }}
            />
          )}
        </Flex>
        {images && (
          <Flex
            sx={{
              gridColumn: images[1]
                ? ['1 / -1', null, '1 / 7']
                : [null, '1 / -1', '1 / 7'],
              gridRow: images[1] ? '1 / 2' : [null, '2 / 3', '1 / 2'],
              maxHeight: images[1] ? ['269px', null, 'none'] : 'none',
            }}
          >
            {images[0] && (
              <Box
                sx={{
                  display: images[1] ? ['none', null, 'flex'] : 'flex',
                  height: '100%',
                  width: '100%',
                }}
              >
                <GatsbyImage
                  image={images[0].gatsbyImageData}
                  key={`sweepstakes-image-large`}
                  alt={images[0].description}
                  style={{
                    height: 'auto',
                    width: '100%',
                  }}
                />
              </Box>
            )}
            {images[1] && (
              <Box
                sx={{
                  display: ['flex', null, 'none'],
                  height: '100%',
                  width: '100%',
                }}
              >
                <GatsbyImage
                  image={images[1].gatsbyImageData}
                  key={`sweepstakes-image-small`}
                  alt={images[1].description}
                  style={{
                    height: 'auto',
                    width: '100%',
                  }}
                />
              </Box>
            )}
          </Flex>
        )}
      </Grid>
    </Box>
  )
}

export default SweepstakesEmailCaptureSection
